"use client";
import {
  Download,
  Layers3,
  LoaderCircle,
} from "lucide-react";
import JSZip from "jszip";
import {
  useState,
} from "react";
import {
  formatBytes,
} from "../../shared/format";
import {
  downloadBlob,
} from "../../shared/io";
import {
  type BatchScanItem,
} from "../../shared/types";


export function BatchQueueView({
  items,
  onOpen,
  onToast,
}: {
  items: BatchScanItem[];
  onOpen: (item: BatchScanItem) => void;
  onToast: (message: string) => void;
}) {
  const [exporting, setExporting] = useState(false);
  const complete = items.filter((item) => item.session);
  const exportBatch = async () => {
    if (!complete.length || exporting) return;
    setExporting(true);
    try {
      const archive = new JSZip();
      const summary = complete.map((item) => ({
        fileName: item.fileName,
        sha256: item.session!.report.hashes.sha256,
        verdict: item.session!.report.verdict,
        score: item.session!.report.score,
        findings: item.session!.report.findings.length,
      }));
      archive.file("batch-summary.json", JSON.stringify(summary, null, 2));
      archive.file(
        "batch-summary.csv",
        [
          "file_name,sha256,verdict,score,findings",
          ...summary.map((item) =>
            [
              JSON.stringify(item.fileName),
              item.sha256,
              JSON.stringify(item.verdict),
              item.score,
              item.findings,
            ].join(","),
          ),
        ].join("\n"),
      );
      for (const item of complete) {
        archive.file(
          `reports/${item.session!.report.id}-${item.fileName}.json`,
          JSON.stringify(item.session!.report, null, 2),
        );
      }
      const output = await archive.generateAsync({
        type: "uint8array",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });
      downloadBlob(output, "jarlab-batch-reports.zip", "application/zip");
      onToast("Batch report bundle downloaded");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="view-stack">
      <section className="panel">
        <div className="panel-heading">
          <div>
            <div className="section-kicker">BULK ANALYSIS</div>
            <h3>Batch queue</h3>
          </div>
          <button
            className="secondary-button compact"
            type="button"
            disabled={!complete.length || exporting}
            onClick={() => void exportBatch()}
          >
            <Download size={14} />
            {exporting ? "Building…" : "Export all"}
          </button>
        </div>
        {items.length ? (
          <div className="batch-table">
            <div className="batch-row batch-header">
              <span>FILE</span>
              <span>STATUS</span>
              <span>VERDICT</span>
              <span>SCORE</span>
              <span />
            </div>
            {items.map((item) => (
              <div className="batch-row" key={item.id}>
                <span>
                  <strong title={item.fileName}>{item.fileName}</strong>
                  <small>{formatBytes(item.size)}</small>
                </span>
                <span className={`batch-status is-${item.status}`}>
                  {item.status === "analyzing" && <LoaderCircle size={12} />}
                  {item.status}
                </span>
                <span>{item.session?.report.verdict ?? item.error ?? "Waiting"}</span>
                <strong>{item.session ? `${item.session.report.score}/100` : "—"}</strong>
                <button
                  type="button"
                  disabled={!item.session}
                  onClick={() => onOpen(item)}
                >
                  Open
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-panel">
            <Layers3 size={25} />
            <strong>No batch yet</strong>
            <span>Select or drop multiple files to analyze up to 20 sequentially.</span>
          </div>
        )}
      </section>
    </div>
  );
}
